/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 * Supports role-based access control for admin and manufacturer separation
 */

import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { LoadingPage } from './ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireManufacturer?: boolean;
  requireManufacturerOnly?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requireAdmin = false, 
  requireManufacturer = false,
  requireManufacturerOnly = false
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingPage text="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin (roleLevel -1)
  const isAdmin = user.roleLevel === -1;
  const isOperationManager = !!user.isOperationManager;
  const isManufacturer = user.roleLevel === 0 && !isOperationManager;
  const isManufacturerOrOperationManager =
    user.roleLevel === 0 &&
    (!isOperationManager || user.operationManagerStatus === 'active');

  // Role-based access control
  if (requireAdmin && !isAdmin) {
    // Admin required but user is not admin - redirect to manufacturer dashboard
    return <Navigate to="/manufacturer" replace />;
  }

  if (requireManufacturerOnly && !isManufacturer) {
    // Manufacturer-only required
    return <Navigate to={isAdmin ? "/admin" : "/manufacturer"} replace />;
  }

  if (requireManufacturer && !isManufacturer) {
    if (!isManufacturerOrOperationManager) {
      return <Navigate to={isAdmin ? "/admin" : "/login"} replace />;
    }
  }

  return <>{children}</>;
}
